import { normalizeCarType } from "@eurodrive/utils"
import { DealType, SearchFilters, defaultSearchFilters } from "./types"

/**
 * URL parameter names mapping to filter properties
 */
export const URL_PARAM_MAP = {
  carType: "carType",
  dealType: "dealType",
  features: "features",
  carTypes: "carTypes",
  priceMin: "priceMin",
  priceMax: "priceMax",
  manufacturer: "manufacturer",
  model: "model",
  yearFrom: "yearFrom",
  yearTo: "yearTo",
  kmFrom: "kmFrom",
  kmTo: "kmTo",
  search: "search",
  discount: "discount",
} as const

/**
 * Checks if URL represents an unfiltered leasing search (just dealType=leasing, no other filters)
 * Used to determine if we should auto-select a carType with inventory
 */
export function isUnfilteredLeasingUrl(
  searchParams: URLSearchParams | Record<string, string | string[] | undefined>,
): boolean {
  const get = (key: string): string | null => {
    if (searchParams instanceof URLSearchParams) {
      return searchParams.get(key)
    }
    const val = searchParams[key]
    return typeof val === "string" ? val : null
  }

  const dealType = get(URL_PARAM_MAP.dealType)
  if (dealType !== "leasing") return false

  // Check if any other meaningful filters are present
  const hasCarType = !!get(URL_PARAM_MAP.carType)
  const hasManufacturer = !!get(URL_PARAM_MAP.manufacturer)
  const hasModel = !!get(URL_PARAM_MAP.model)
  const hasFeatures = !!get(URL_PARAM_MAP.features)
  const hasCarTypes = !!get(URL_PARAM_MAP.carTypes)
  const hasPriceMin = !!get(URL_PARAM_MAP.priceMin)
  const hasPriceMax = !!get(URL_PARAM_MAP.priceMax)
  const hasSearch = !!get(URL_PARAM_MAP.search)
  const hasDiscount = !!get(URL_PARAM_MAP.discount)

  return (
    !hasCarType &&
    !hasManufacturer &&
    !hasModel &&
    !hasFeatures &&
    !hasCarTypes &&
    !hasPriceMin &&
    !hasPriceMax &&
    !hasSearch &&
    !hasDiscount
  )
}

/**
 * Validates if a string is a valid DealType
 */
const isValidDealType = (value: string): value is DealType => {
  return ["new", "used", "leasing"].includes(value)
}

/**
 * Safely parses a comma-separated string into an array of strings
 */
const parseCommaSeparatedIds = (value: string | null): string[] => {
  if (!value || value.trim() === "") return []
  return value
    .split(",")
    .map((id) => id.trim())
    .filter((id) => id !== "")
}

/**
 * Safely parses a numeric string, returns null if invalid
 */
const parseNumber = (
  value: string | null,
  min?: number,
  max?: number,
): number | null => {
  if (!value || value.trim() === "") return null
  const num = parseInt(value, 10)
  if (isNaN(num)) return null
  if (min !== undefined && num < min) return null
  if (max !== undefined && num > max) return null
  return num
}

/**
 * Converts URL search parameters to SearchFilters object
 */
export const urlParamsToFilters = (
  searchParams: URLSearchParams,
): SearchFilters => {
  const filters: SearchFilters = { ...defaultSearchFilters }

  // Parse carType. Pre-END-86 values ("Luxury", "Basic") still appear in
  // shared links and CMS content, so they are normalised to a merged category.
  const carType = normalizeCarType(searchParams.get(URL_PARAM_MAP.carType))
  if (carType) {
    filters.carType = carType
  }

  // Parse dealType
  const dealType = searchParams.get(URL_PARAM_MAP.dealType)
  if (dealType && isValidDealType(dealType)) {
    filters.dealType = dealType
  }

  // Parse selected features
  const features = searchParams.get(URL_PARAM_MAP.features)
  filters.selectedFeatures = parseCommaSeparatedIds(features)

  // Parse selected car types
  const carTypes = searchParams.get(URL_PARAM_MAP.carTypes)
  filters.selectedCarTypes = parseCommaSeparatedIds(carTypes)

  // Parse price range
  const priceMin = parseNumber(searchParams.get(URL_PARAM_MAP.priceMin), 0)
  const priceMax = parseNumber(searchParams.get(URL_PARAM_MAP.priceMax), 0)

  if (priceMin !== null && priceMax !== null && priceMin <= priceMax) {
    filters.priceRange = [priceMin, priceMax]
  } else if (priceMin !== null) {
    filters.priceRange = [priceMin, filters.priceRange[1]]
  } else if (priceMax !== null) {
    filters.priceRange = [filters.priceRange[0], priceMax]
  }

  // Parse manufacturer
  const manufacturer = searchParams.get(URL_PARAM_MAP.manufacturer)
  if (manufacturer && manufacturer.trim() !== "") {
    filters.selectedManufacturer = manufacturer.trim()
  }

  // Parse model
  const model = searchParams.get(URL_PARAM_MAP.model)
  if (model && model.trim() !== "") {
    filters.selectedModel = model.trim()
  }

  // Parse year range
  const yearFrom = searchParams.get(URL_PARAM_MAP.yearFrom)
  if (yearFrom && yearFrom.trim() !== "") {
    const year = parseNumber(yearFrom)
    if (year !== null) {
      filters.yearFrom = year.toString()
    }
  }

  const yearTo = searchParams.get(URL_PARAM_MAP.yearTo)
  if (yearTo && yearTo.trim() !== "") {
    const year = parseNumber(yearTo)
    if (year !== null) {
      filters.yearTo = year.toString()
    }
  }

  // Parse km range
  const kmFrom = searchParams.get(URL_PARAM_MAP.kmFrom)
  if (kmFrom && kmFrom.trim() !== "") {
    const km = parseNumber(kmFrom, 0)
    if (km !== null) {
      filters.kmFrom = km.toString()
    }
  }

  const kmTo = searchParams.get(URL_PARAM_MAP.kmTo)
  if (kmTo && kmTo.trim() !== "") {
    const km = parseNumber(kmTo, 0)
    if (km !== null) {
      filters.kmTo = km.toString()
    }
  }

  // Parse search query
  const searchQuery = searchParams.get(URL_PARAM_MAP.search)
  if (searchQuery && searchQuery.trim() !== "") {
    filters.searchQuery = searchQuery.trim()
  }

  // Parse discount filter
  const discount = searchParams.get(URL_PARAM_MAP.discount)
  if (discount === "true") {
    filters.showDiscountsOnly = true
  }

  return filters
}

/**
 * Converts SearchFilters object to URL search parameters.
 * `metadataBounds` are the active category's price bounds — an untouched
 * slider (range equal to its bounds) must not bake priceMin/priceMax into
 * the URL, otherwise fallback bounds (0/9999999 when metadata is missing)
 * leak into shareable URLs and later get re-ingested as a "narrowed" range.
 */
export const filtersToUrlParams = (
  filters: SearchFilters,
  metadataBounds?: { minimumPrice: number; maximumPrice: number } | null,
): URLSearchParams => {
  const params = new URLSearchParams()

  // Only add non-default values to URL to keep it clean
  if (filters.carType !== defaultSearchFilters.carType) {
    params.set(URL_PARAM_MAP.carType, filters.carType)
  }

  if (filters.dealType !== defaultSearchFilters.dealType) {
    params.set(URL_PARAM_MAP.dealType, filters.dealType)
  }

  if (filters.selectedFeatures.length > 0) {
    params.set(URL_PARAM_MAP.features, filters.selectedFeatures.join(","))
  }

  if (filters.selectedCarTypes.length > 0) {
    params.set(URL_PARAM_MAP.carTypes, filters.selectedCarTypes.join(","))
  }

  // Only add price range if the user actually narrowed it: skip the unset
  // default ([0, 0]) and a range that still spans the full metadata bounds
  const isDefaultPriceRange =
    filters.priceRange[0] === defaultSearchFilters.priceRange[0] &&
    filters.priceRange[1] === defaultSearchFilters.priceRange[1]
  const spansFullMetadataBounds =
    !!metadataBounds &&
    filters.priceRange[0] === metadataBounds.minimumPrice &&
    filters.priceRange[1] === metadataBounds.maximumPrice
  if (!isDefaultPriceRange && !spansFullMetadataBounds) {
    params.set(URL_PARAM_MAP.priceMin, filters.priceRange[0].toString())
    params.set(URL_PARAM_MAP.priceMax, filters.priceRange[1].toString())
  }

  if (filters.selectedManufacturer) {
    params.set(URL_PARAM_MAP.manufacturer, filters.selectedManufacturer)
  }

  if (filters.selectedModel) {
    params.set(URL_PARAM_MAP.model, filters.selectedModel)
  }

  if (filters.yearFrom && filters.yearFrom !== defaultSearchFilters.yearFrom) {
    params.set(URL_PARAM_MAP.yearFrom, filters.yearFrom)
  }

  if (filters.yearTo && filters.yearTo !== defaultSearchFilters.yearTo) {
    params.set(URL_PARAM_MAP.yearTo, filters.yearTo)
  }

  if (filters.kmFrom && filters.kmFrom !== defaultSearchFilters.kmFrom) {
    params.set(URL_PARAM_MAP.kmFrom, filters.kmFrom)
  }

  if (filters.kmTo && filters.kmTo !== defaultSearchFilters.kmTo) {
    params.set(URL_PARAM_MAP.kmTo, filters.kmTo)
  }

  if (
    filters.searchQuery &&
    filters.searchQuery !== defaultSearchFilters.searchQuery
  ) {
    params.set(URL_PARAM_MAP.search, filters.searchQuery)
  }

  if (filters.showDiscountsOnly) {
    params.set(URL_PARAM_MAP.discount, "true")
  }

  return params
}

/**
 * Checks if two filter objects are equal (for preventing unnecessary URL updates)
 */
export const areFiltersEqual = (
  filters1: SearchFilters,
  filters2: SearchFilters,
): boolean => {
  return (
    filters1.carType === filters2.carType &&
    filters1.dealType === filters2.dealType &&
    JSON.stringify(filters1.selectedFeatures.sort()) ===
      JSON.stringify(filters2.selectedFeatures.sort()) &&
    JSON.stringify(filters1.selectedCarTypes.sort()) ===
      JSON.stringify(filters2.selectedCarTypes.sort()) &&
    filters1.priceRange[0] === filters2.priceRange[0] &&
    filters1.priceRange[1] === filters2.priceRange[1] &&
    filters1.selectedManufacturer === filters2.selectedManufacturer &&
    filters1.selectedModel === filters2.selectedModel &&
    filters1.yearFrom === filters2.yearFrom &&
    filters1.yearTo === filters2.yearTo &&
    filters1.kmFrom === filters2.kmFrom &&
    filters1.kmTo === filters2.kmTo &&
    filters1.searchQuery === filters2.searchQuery
  )
}
